import { Component, AfterViewInit, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ToastClassEnum } from 'src/app/core/services/snackbar/snackbar.models';
import { SnackbarService } from 'src/app/core/services/snackbar/snackbar.service';
import { AccountAdvisorModel, AccountClientModel, AccountMessageRequestTypeEnum, AccountModel, AccountRequestCustomHoldingModel, AccountRequestDetailModel, AccountRequestFundingSleeveModel, AccountRequestsClient, AccountRequestTradeOrderModel, CloseAccountRequestDetailModel, CloseAccountRequestsClient, ContributionRequestDetailModel, ContributionRequestsClient, DistributionRequestDetailModel, DistributionRequestsClient, FileModel, GeneralAccountRequestDetailModel, InvestmentAccountsClient, OrionServiceRequestModel2, OrionServiceRequestsClient, PlanningDeskAccountModel, PlanningDeskRequestDetailModel, PlanningDeskRequestsClient, ReallocationRequestDetailModel, ReallocationRequestsClient, RequestModel, RequestNoteModel, RequestsClient, SleeveModel, TradeModel } from 'src/app/core/clients/generated/client';
import { MessageAndFileModel, RequestTrackerTypeEnum } from '../../models/request-tracker-models';
import { fadeIn } from 'src/app/shared/constants/animations';
import { FileMessageUploadAttachedRequest, FileMessageUploadModel } from '../../../../../shared/models/files-messages.model';
import { Account } from '../../../accounts/models/account.models';
import { formatCurrency, formatPercent } from '@angular/common';

@Component({
  animations: [fadeIn],
  selector: 'app-request-tracker-detail--modal',
  templateUrl: './request-tracker-detail-modal.component.html',
})
export class RequestTrackerDetailModalComponent implements OnInit, AfterViewInit {
  constructor(
    private reallocationRequestsApiService: ReallocationRequestsClient,
    private distributionRequestsApiService: DistributionRequestsClient,
    private closeAccountRequestsApiService: CloseAccountRequestsClient,
    private contributionRequestsApiService: ContributionRequestsClient,
    private planningDeskRequestsApiService: PlanningDeskRequestsClient,
    private orionServiceRequestsApiService: OrionServiceRequestsClient,
    private generalAccountRequestsApiService: RequestsClient,
    private accountRequestsApiService: AccountRequestsClient,
    private snackbarService: SnackbarService,
    private investmentAccountApi: InvestmentAccountsClient,
    public dialogRef: MatDialogRef<RequestTrackerDetailModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: RequestModel,
  ) { }

  clients: AccountClientModel[] = [];
  advisors: AccountAdvisorModel[] = [];
  clientTitle = RequestTrackerTypeEnum.Clients;
  details = new Map<string, unknown>();
  // [req]-backed requests use one generic route whatever their type, so the prefix is set explicitly
  // rather than derived from the category string like the legacy types. Deriving it would happen to
  // work for 'Initial Account' but would misroute future types onto the old per-type controllers.
  filePrefix = this.data.accountRequestId
    ? `api/account-requests/${this.data.accountRequestId}/files/`
    : this.data.investmentAccountUploadId ? `api/requests/${this.data.category.replace('-', '').replace(/\s+/g, '-').toLowerCase()}/${this.data.investmentAccountUploadId}/files/` : `api/${this.data.category.replace('-', '').replace(/\s+/g, '-').toLowerCase()}-requests/${this.data.investmentAccountModelRequestId || this.data.investmentAccountDistributionRequestId || this.data.portfolioAnalysisRequestId || this.data.orionServiceRequestId}/files/`;
  grossSleeveAmount = 0.0;
  grossCurrentSleeveAmount = 0.0;
  note = '';
  files: FileModel[] = [];
  notes: RequestNoteModel[] = [];
  notesAndFiles: MessageAndFileModel[] = [];
  tradeInstructions = '';
  sleeves: SleeveModel[] = [];
  trades: TradeModel[] = [];
  /**
   * Cash positions this request draws on, at the amount released.
   *
   * ⚠️ Empty on anything filed before ACMTECH-981, which does NOT mean no cash was freed -- the
   * portal did not send it then. The section simply does not render, which is the same thing a
   * request that freed nothing shows.
   */
  releasedCash: AccountRequestTradeOrderModel[] = [];
  sells: AccountRequestTradeOrderModel[] = [];
  buys: AccountRequestTradeOrderModel[] = [];
  customHoldings: AccountRequestCustomHoldingModel[] = [];
  currentSleeves: SleeveModel[] = [];
  planningDeskAccounts: PlanningDeskAccountModel[] = [];
  sleevesTitle = RequestTrackerTypeEnum.Sleeves;
  tradesTitle = RequestTrackerTypeEnum.Trades;
  accountsTitle = RequestTrackerTypeEnum.Accounts;
  cashUsedTitle = RequestTrackerTypeEnum.CashUsed;
  positionsSoldTitle = RequestTrackerTypeEnum.PositionsSold;
  positionsBoughtTitle = RequestTrackerTypeEnum.PositionsBought;
  currentAllocationTitle = RequestTrackerTypeEnum.CurrentAllocation;
  customHoldingsTitle = RequestTrackerTypeEnum.CustomHoldings;
  requestTrackerTypeEnum = RequestTrackerTypeEnum;
  message?: FileMessageUploadModel;

  /**
   * Keys that head a table-style section rather than being a label/value pair.
   *
   * The three places in the template that care used to each carry their own `!==` chain over the
   * same five titles, so adding a section meant editing three lists and the failure mode was a
   * header silently rendering as an ordinary row. One list, one place to add.
   */
  private static readonly SECTION_HEADER_TITLES: readonly string[] = [
    RequestTrackerTypeEnum.Sleeves,
    RequestTrackerTypeEnum.Accounts,
    RequestTrackerTypeEnum.Trades,
    RequestTrackerTypeEnum.PositionsSold,
    RequestTrackerTypeEnum.PositionsBought,
    RequestTrackerTypeEnum.CurrentAllocation,
    RequestTrackerTypeEnum.CustomHoldings,
  ];

  isSectionHeader(key: string): boolean {
    return RequestTrackerDetailModalComponent.SECTION_HEADER_TITLES.includes(key);
  }

  /**
   * A money row for the v1 detail paths, where every amount is optional.
   *
   * 🔴 THESE USED TO BE SET TWICE: the map took an empty value as a marker, the figure went on a
   * component field, and a dedicated template block rendered it. A V2 distribution sets the same
   * 'Gross Distribution Amount' key to a real value -- so the generic loop and the dedicated block
   * both fired and the row read '$10,000.00 $0.00', the zero being the field's initialiser on a
   * path that never assigned it. One mechanism now, and the V2 setter already used it.
   *
   * Absent prints nothing rather than $0.00, which is what the currency pipe did on an undefined
   * field: v1 leaves the figure off a request that never carried one, and $0.00 is a figure an
   * advisor could have entered.
   */
  private setCurrencyDetail(key: string, amount: number | undefined): void {
    this.details.set(key, amount != null ? formatCurrency(amount, 'en-US', '$') : '');
  }

  /**
   * Whether this row is backed by [req].AccountRequest, of any type.
   *
   * The template used to stand in for this with `category !== 'Initial Account'`, which held only
   * while type 1 was the sole [req] type. A Reallocation V2 carries category 'Reallocation', so it
   * fell into the v1 branch and rendered that branch's empty "Original Account Setup" scaffolding —
   * `setAccountRequestDetailObject` never populates those fields. Keyed on the id, every present and
   * future [req] type lands in the right branch.
   */
  get isAccountRequest(): boolean {
    return this.data.accountRequestId != null;
  }

  /**
   * Whether to render the Request Notes panel at all.
   *
   * 🔴 A V2 REQUEST WITH NO NOTE SHOWS NOTHING, rather than the heading over a grey "No notes"
   * (AndyP, 2026-10-01). The V2 wizards have no note box -- the step exists but its notes section
   * is switched off at `REALLOCATION_V2_REQUEST_NOTES_ENABLED` -- so the panel was captioning a
   * field the advisor was never offered and could not have filled. "No notes" reads as "they
   * chose not to write one", which is a different and untrue statement.
   *
   * ⚠️ NOT a blanket "hide when empty". A v1 request DOES have a note box, so an empty one there
   * is a real answer worth printing: the advisor had the option and passed on it. Keyed on
   * {@link isAccountRequest}, which is exactly the V2 family -- every [req].AccountRequest type,
   * and nothing v1 reaches it.
   *
   * 🟢 A V2 request that somehow HAS a note still shows it. CRM writes the task description from
   * this field, and switching the notes flag on would start populating it, so this gates on the
   * note being absent rather than on the request being V2.
   */
  get showRequestNotes(): boolean {
    const hasNote = (this.note ?? '').trim().length > 0;
    return hasNote || !this.isAccountRequest;
  }

  statusObj = {
    Show: false,
    Status: '',
    StatusDate: '',
    StatusNote: '',
    StatusId: 0,
  };

  ngOnInit(): void {
    this.getData();
  }

  ngAfterViewInit(): void {
    const width = '166rem';
    const height = '84rem';
    this.dialogRef.updateSize(width, height);
  }

  getData(): void {
    console.log('init');
    this.note = '';
    this.tradeInstructions = '';
    this.clients = [];
    // getTotalSleeves() accumulates, and getData() re-runs after a message is sent, so this has to
    // be cleared or the sleeve percentages shrink on every refresh.
    this.grossSleeveAmount = 0;
    this.grossCurrentSleeveAmount = 0;
    this.details.clear();
    this.initFileMessageComponent(this.data);

    // [req].AccountRequest rows dispatch on the type id, the same way CRM's own list dispatchers do,
    // so future types (reallocation/contribution/distribution) slot into this switch rather than
    // adding another category-string branch below.
    if (this.data.accountRequestId != null) {
      switch (this.data.accountRequestTypeId) {
        case 1: // Initial Account
        case 2: // Reallocation V2
        case 3: // Contribution V2
        case 4: // Distribution V2 (a Close Account is one of these, flagged)
          // All four types share one fetch and one mapper: the endpoint is keyed on AccountRequestID
          // and type-agnostic, and every section is gated on its array being non-empty, so a type
          // difference shows up as a section appearing or not rather than as a branch. Each type fell
          // to default before it was listed, which opened an empty modal behind a "cannot be
          // displayed yet" toast.
          //
          // ⚠️ The `category === Distribution / Contribution` branches BELOW are v1's, keyed on
          // investmentAccountDistributionRequestId, and a V2 request never reaches them — it is
          // caught by the accountRequestId test above. The two generations share a category string
          // deliberately, because to an advisor they are one request type.
          //
          // Types 3 and 4 additionally carry a funding block, which `setAccountRequestDetailObject`
          // renders — see the FUNDING section there.
          this.accountRequestsApiService.getAccountRequestDetail(this.data.accountRequestId).subscribe({
            next: (details) => {
              this.setAccountRequestDetailObject(details);
            }, error: () => {
              this.snackbarService.openSnackbar('Error retrieving request information', ToastClassEnum.warning);
            }
          });
          break;
        default:
          this.snackbarService.openSnackbar('This request type cannot be displayed yet', ToastClassEnum.warning);
          break;
      }
    } else if (this.data.category === RequestTrackerTypeEnum.Reallocation) {
      if (this.data.investmentAccountModelRequestId != null)
        this.reallocationRequestsApiService.getReallocationRequestDetail(this.data.investmentAccountModelRequestId).subscribe({
          next: (details) => {
            this.setRequestDetailObject(details);
          }, error: () => {
            this.snackbarService.openSnackbar('Error retrieving agent information', ToastClassEnum.warning);
          }
        });
    } else if (this.data.category === RequestTrackerTypeEnum.CloseAccount) {
      if (this.data.investmentAccountDistributionRequestId != null)
        this.closeAccountRequestsApiService.getCloseAccountRequestDetail(this.data.investmentAccountDistributionRequestId).subscribe({
          next: (details) => {
            this.setRequestDetailObject(details);
          }, error: () => {
            this.snackbarService.openSnackbar('Error retrieving agent information', ToastClassEnum.warning);
          }
        });
    } else if (this.data.category === RequestTrackerTypeEnum.Distribution) {
      if (this.data.investmentAccountDistributionRequestId != null)
        this.distributionRequestsApiService.getDistributionRequestDetail(this.data.investmentAccountDistributionRequestId).subscribe({
          next: (details) => {
            this.setRequestDetailObject(details);
          }, error: () => {
            this.snackbarService.openSnackbar('Error retrieving agent information', ToastClassEnum.warning);
          }
        });
    } else if (this.data.category === RequestTrackerTypeEnum.Contribution) {
      if (this.data.investmentAccountDistributionRequestId != null)
        this.contributionRequestsApiService.getContributionRequestDetail(this.data.investmentAccountDistributionRequestId).subscribe({
          next: (details) => {
            this.setRequestDetailObject(details);
          }, error: () => {
            this.snackbarService.openSnackbar('Error retrieving agent information', ToastClassEnum.warning);
          }
        });
    } else if (this.data.category === RequestTrackerTypeEnum.PortfolioAnalyisDesk) {
      if (this.data.portfolioAnalysisRequestId != null) {
        console.log('test');
        this.planningDeskRequestsApiService.getPlanningDeskRequestDetail(this.data.portfolioAnalysisRequestId).subscribe({
          next: (details) => {
            this.setPlanningDeskDetailObject(details);
          }, error: () => {
            this.snackbarService.openSnackbar('Error retrieving agent information', ToastClassEnum.warning);
          }
        });
      }
    } else if (this.data.category === RequestTrackerTypeEnum.OrionServiceRequest) {
      if (this.data.orionServiceRequestId != null)
        this.orionServiceRequestsApiService.getOrionServiceRequestDetail(this.data.orionServiceRequestId).subscribe({
          next: (details) => {
            this.setOrionServiceRequestDetailObject(details);
          }, error: () => {
            this.snackbarService.openSnackbar('Error retrieving agent information', ToastClassEnum.warning);
          }
        });
    } else if (this.data.category === RequestTrackerTypeEnum.GeneralAccount) {
      if (this.data.investmentAccountUploadId != null)
        this.generalAccountRequestsApiService.getGeneralAccountRequestDetail(this.data.investmentAccountUploadId).subscribe({
          next: (details) => {
            this.setRequestDetailObject(details);
          }, error: () => {
            this.snackbarService.openSnackbar('Error retrieving agent information', ToastClassEnum.warning);
          }
        });
    }
  }

  private initFileMessageComponent(model: RequestModel): void {

    // include model.investmentAccountUploadId as last item incase the request is just an investmentAccountUpload not tied to another request
    // accountRequestId is checked first: a [req] request also carries a taskId, and messages sent
    // against it must resolve back to that request, not fall through to the general-account path.
    const invOnBehalfOfRequest: FileMessageUploadAttachedRequest = {
      requestId: model.accountRequestId ? model.accountRequestId : model.investmentAccountDistributionRequestId ? model.investmentAccountDistributionRequestId : model.investmentAccountModelRequestId ? model.investmentAccountModelRequestId : model.orionServiceRequestId ? model.orionServiceRequestId : model.portfolioAnalysisRequestId ? model.portfolioAnalysisRequestId : model.investmentAccountUploadId ? model.investmentAccountUploadId : undefined,

      requestType: model.accountRequestId ? AccountMessageRequestTypeEnum.InitialAccount : model.investmentAccountDistributionRequestId ? AccountMessageRequestTypeEnum.Distribution : model.investmentAccountModelRequestId ? AccountMessageRequestTypeEnum.ModelAllocation : model.orionServiceRequestId ? AccountMessageRequestTypeEnum.OrionServiceRequest : model.portfolioAnalysisRequestId ? AccountMessageRequestTypeEnum.PlanningDesk : model.investmentAccountUploadId ? AccountMessageRequestTypeEnum.GeneralAccount : undefined,

      taskId: model.taskId,

      requestTitle: model.category + (model.subCategory ? ' - ' + model.subCategory : '')
    };

    if (model.investmentAccountId != null) {
      this.investmentAccountApi.getInvestmentAccount(model.investmentAccountId as number).subscribe({
        next: apiAccount => {
          const invAccount = this.convertInvestmentAccountToAccount(apiAccount);
          this.message = {
            account: invAccount,
            onBehalfOfRequest: invOnBehalfOfRequest,
          } as FileMessageUploadModel;
        },
      });
    } else {
      this.message = {
        account: undefined,
        onBehalfOfRequest: invOnBehalfOfRequest,
      } as FileMessageUploadModel;
    }
  }

  convertInvestmentAccountToAccount(account: AccountModel): Account {
    return {
      Advisors: account.advisors.map(a => {
        return {
          AdvisorId: a.agentID,
          Crd: a.crd,
          FirstName: a.firstName,
          LastName: a.lastName,
        };
      }),
      Clients: account.clients.map(c => {
        return {
          Id: c.id,
          FirstName: c.firstName,
          LastName: c.lastName,
        };
      }),
      Type: account.investmentAccountType,
      Model: account.investmentAccountModel,
      Custodian: account.custodian,
      CustodianId: account.custodianID as number,
      Value: account.accountValue as number,
      CashValue: account.cashValue as number,
      OpenedDate: account.openedOn as Date,
      Fee: RequestTrackerDetailModalComponent.calculateAccountFee(account),
      Status: account.investmentAccountStatus,
      IsInvestmentAccount: true,
      AccountNumber: account.accountNumber,
      Carrier: null,
      Product: null,
      InsuranceAccountId: null,
      InvestmentAccountId: account.investmentAccountID,
      Sleeves: account.sleeves,
      ShowSleeves: false,
      TDSchwabAccountNumbers: account.tdSchwabAccountNumbers,
    };
  }

  private static calculateAccountFee(account: AccountModel): string {
    if (account.totalFee === 0) return '---';
    if (!account.sleeves.length) return formatPercent(account.totalFee ?? 0, 'en-US', '1.2');
    if (account.sleeves.every(a => a.accountTotalFee === account.sleeves[0].accountTotalFee)) // check if all sleeves have same fee
      return formatPercent(account.sleeves[0].accountTotalFee, 'en-US', '1.2');
    return 'Multiple';
  }

  getTotalSleeves(): void {
    this.sleeves.forEach(element => {
      this.grossSleeveAmount += element.amount;
    });
  }

  // Its own total, deliberately not grossSleeveAmount: current-sleeve percentages have to be a share
  // of what the account held BEFORE. Dividing by the requested total would silently rebase them.
  getTotalCurrentSleeves(): void {
    this.currentSleeves.forEach(element => {
      this.grossCurrentSleeveAmount += element.amount;
    });
  }

  /**
   * A null OrionEclipseSecurityID means the advisor typed the ticker and the security master never
   * matched it, so the Trade Team has to vet it before the order can be placed. Derived from the
   * absent id rather than a separate flag, so the two cannot drift apart.
   */
  needsTradeTeamReview(order: AccountRequestTradeOrderModel): boolean {
    return order.orionEclipseSecurityID == null;
  }

  /**
   * Eclipse security types whose SALE proceeds may be reallocated inside one request: 1 Mutual
   * Fund, 2 CASH, 5 Equity. Anything else is sold normally and the money held back.
   *
   * ⚠️ ECLIPSE's ids, not CRM's `req.SecurityType.SecurityTypeID`. The two do not line up — CRM 4
   * is Equity where Eclipse 5 is — so testing CRM keys against this list would quietly treat CD
   * and Bond as reallocatable, the exact opposite of the rule. The wizard, the CRM tracker and
   * this screen all express it in Eclipse ids, which is what stops the three drifting.
   */
  private static readonly REALLOCATABLE_ECLIPSE_TYPE_IDS = new Set([1, 2, 5]);

  /**
   * Whether a sell's proceeds are held back rather than freed to spend inside this request.
   *
   * ⚠️ DERIVED, not sent. What matters — the amount withheld — is already pinned as its own
   * `PENDING CASH - SELLS` custom-holding line, so the record does not depend on this. It only
   * says WHICH sales caused it, which is presentation; that is what makes deriving acceptable
   * here where it would not be for the money.
   *
   * ⚠️ A null type reads as NOT reviewable, and that is right rather than a fudge: before this
   * rule only the three reallocatable types could be sold at all, so anything filed earlier is
   * reallocatable by construction. Same reason the CRM tracker treats null the same way.
   */
  requiresSellReview(order: AccountRequestTradeOrderModel): boolean {
    if (order.orionEclipseSecurityTypeID == null) return false;
    return !RequestTrackerDetailModalComponent.REALLOCATABLE_ECLIPSE_TYPE_IDS.has(
      order.orionEclipseSecurityTypeID,
    );
  }

  get hasSellsNeedingReview(): boolean {
    return this.sells.some(s => this.requiresSellReview(s));
  }

  /**
   * A needs-review buy carries no price, so it contributes nothing and is excluded outright rather
   * than added as a zero — the footnote then tells the advisor the total is short.
   */
  get totalBuyValue(): number {
    return this.buys
      .filter(b => !this.needsTradeTeamReview(b))
      .reduce((sum, b) => sum + (b.estimatedValue ?? 0), 0);
  }

  get hasBuysNeedingReview(): boolean {
    return this.buys.some(b => this.needsTradeTeamReview(b));
  }
  /**
   * Tickers the wizard writes for its SYNTHETIC cash line, matched as literals.
   *
   * That row is invented by the portal (1 share = $1) rather than resolved from Orion, so it has
   * no security id by construction — exactly like a hand-typed holding, which is why the id
   * alone cannot tell the two apart.
   *
   * ⚠️ TWO of them, and the second is a bug fix. The wizard used to write `CASH`; it now writes
   * `PROTECTED CASH`, so that it cannot be confused with the account's own cash holding, which
   * Orion also tickers `CASH`. This literal was not updated with it, so every request filed
   * since fell through to {@link needsReviewHolding} and the protected-cash line was shown
   * flagged for Trade Team review — work that does not exist, on the one row nobody entered.
   *
   * Both are kept: requests filed before the rename carry the old ticker for good.
   *
   * ⚠️ THREE now. `PENDING CASH - SELLS` carries proceeds the firm holds back when a security
   * outside Mutual Fund / CASH / Equity is sold. It is synthetic like the other two, so it hits
   * the same trap: left out, it has no security id, reads as hand-typed, and gets badged for
   * Trade Team review — inventing work on a line nobody entered. Any future synthetic cash line
   * belongs here on the day it is added, not after someone notices the badge.
   *
   * ⚠️ FIVE, after two renames on 2026-09-15: `SET-ASIDE CASH` for the advisor's own step-4
   * amount (was `PROTECTED CASH`), and `PENDING SELLS` for proceeds held back from a sale (was
   * `PENDING CASH - SELLS`). **Every earlier spelling stays**, and not as tidiness: requests
   * filed before each rename carry the old ticker for good, and dropping one would badge every
   * one of them.
   *
   * ⚠️ `Pending Buys` is NOT here and must not be added. It is a LABEL the wizard prints over
   * the sum of the pending buy rows on its own review page; nothing is ever filed under it. Those
   * rows reach CRM under the tickers the advisor typed, and being unrecognised is precisely what
   * marks them for review.
   */
  private static readonly CASH_TICKERS = new Set([
    'CASH', 'PROTECTED CASH', 'PENDING CASH - SELLS', 'SET-ASIDE CASH', 'PENDING SELLS',
  ]);

  /**
   * A custom holding the Trade Team still has to resolve.
   *
   * Same rule as {@link needsTradeTeamReview} for trade orders, minus the synthetic cash line. A
   * buy whose ticker never matched the security master cannot be executed, so its money is held
   * as cash until they identify it.
   */
  needsReviewHolding(holding: AccountRequestCustomHoldingModel): boolean {
    return holding.orionEclipseSecurityID == null && !this.isCashHolding(holding);
  }

  /** Holdings the account will actually own as securities. */
  get resolvedHoldings(): AccountRequestCustomHoldingModel[] {
    return this.customHoldings.filter(h => !this.needsReviewHolding(h) && !this.isCashHolding(h));
  }

  /**
   * Buys the Trade Team still has to identify, as their own group.
   *
   * ⚠️ Split out of protected cash (feedback, 2026-09-11). These were listed under the CASH
   * heading with a small "requires review" note, because arithmetically they are cash — an
   * unresolved buy is held as cash until someone resolves it. Grouped there they read as a
   * BALANCE, when they are a JOB: work outstanding before the request can be executed. Their own
   * heading and their own total says so without relying on a note being noticed.
   */
  get pendingReviewHoldings(): AccountRequestCustomHoldingModel[] {
    return this.customHoldings.filter(h => this.needsReviewHolding(h));
  }

  get pendingReviewTotal(): number {
    return this.pendingReviewHoldings.reduce((sum, h) => sum + (h.estimatedValue ?? 0), 0);
  }

  /**
   * Every share of this position is being sold, rather than some of them.
   *
   * ⚠️ `TradeActionID` 3, not "quantity equals the holding". Sell All was made its own action
   * precisely because closing a position out and trimming one are materially different trades
   * (ACMTECH-949), and the advisor's own tracker did not say which — the figure alone cannot,
   * since the tracker never sees what the account held before.
   *
   * 🟢 The data was already on the wire. FIGData's sells query returns actions 2 AND 3; it
   * filtered to 2 alone until 2026-09-15, which hid every fully liquidated position outright.
   */
  isSellAll(order: AccountRequestTradeOrderModel): boolean {
    return order.tradeActionID === 3;
  }

  /**
   * Σ custodial cash this request frees.
   *
   * ⚠️ NOT added to {@link accountTotal}, and not to anything else. These dollars were inside the
   * sleeve before the request and are inside the models or the sleeve after it, depending on what
   * the advisor did with them -- adding this would count the same money twice and draw the account
   * larger than it is. It answers "where did the funding come from", not "what is the account
   * worth".
   *
   * 🟢 Each row pairs with the kept remainder of the same position in {@link customHoldings}, so
   * the two add back to what the account held.
   */
  get totalCashReleased(): number {
    return this.releasedCash.reduce((sum, c) => sum + (c.estimatedValue ?? 0), 0);
  }

  /**
   * Σ estimated proceeds of everything being sold.
   *
   * ⚠️ Includes rows flagged Review Required, unlike {@link totalBuyValue}, and the asymmetry is
   * real rather than an oversight: a pending BUY carries no price at all, so it can only be
   * excluded or added as a lie, where a reviewed SELL has a perfectly good estimate — the money
   * simply cannot be spent in this request. Leaving it out would make the total disagree with
   * the rows above it.
   */
  get totalSellValue(): number {
    return this.sells.reduce((sum, s) => sum + (s.estimatedValue ?? 0), 0);
  }

  /**
   * What the Custom Holdings sleeve is worth — securities kept or bought, plus every synthetic
   * cash line the wizard wrote.
   *
   * ⚠️ The WHOLE array, deliberately, including the cash on hold. This answers "what is in the
   * sleeve", which is a different question from the bar's Custom Holdings run: that one subtracts
   * cash on hold so money MOVING between them is visible while the advisor works. Using the
   * narrower figure here would make {@link accountTotal} come up short by exactly the holds.
   */
  get customHoldingsTotal(): number {
    return this.customHoldings.reduce((sum, h) => sum + (h.estimatedValue ?? 0), 0);
  }

  /**
   * Models plus the sleeve — the whole account.
   *
   * ⚠️ THE ONLY PLACE THIS SCREEN ADDS UP. Every other section totals one instruction — what is
   * sold, what is bought, what is held — so an advisor checking that the request still covers
   * their whole account was adding those by hand. The wizard's review page gained the same row
   * on 2026-09-15 for the same reason.
   */
  get accountTotal(): number {
    return this.grossSleeveAmount + this.customHoldingsTotal;
  }

  /** Σ of the buys the Trade Team still has to identify. */
  get pendingBuysTotal(): number {
    return this.pendingReviewTotal;
  }

  /**
   * Cash the request is holding because a SALE is under review.
   *
   * ⚠️ Found by TICKER, not by an absent security id. This line is synthetic, so it has no
   * security id either — the id test alone cannot tell it from a hand-typed holding, which is
   * the whole reason {@link CASH_TICKERS} exists.
   *
   * ⚠️ Both spellings. `PENDING CASH - SELLS` was renamed `PENDING SELLS` on 2026-09-15 and
   * requests filed before then carry the old one for good.
   */
  private static readonly HELD_CASH_TICKERS = new Set(['PENDING CASH - SELLS', 'PENDING SELLS']);

  get pendingSellsHoldings(): AccountRequestCustomHoldingModel[] {
    return this.customHoldings.filter(h => this.isHeldCashHolding(h));
  }

  get pendingSellsTotal(): number {
    return this.pendingSellsHoldings.reduce((sum, h) => sum + (h.estimatedValue ?? 0), 0);
  }

  /** Everything this request cannot invest because something is unresolved. */
  get cashOnHoldTotal(): number {
    return this.pendingBuysTotal + this.pendingSellsTotal;
  }

  get hasCashOnHold(): boolean {
    return this.pendingReviewHoldings.length > 0 || this.pendingSellsHoldings.length > 0;
  }

  /**
   * SETTLED cash: what the sleeve simply holds. Cash positions the advisor left alone and their
   * own set-aside — nothing has to happen to either.
   *
   * ⚠️ NOT every cash ticker, and that is the fix rather than a refinement (2026-09-15). This
   * returned all of them, under a heading hardcoded to read `CASH` / "Protected Cash" with every
   * child italicised "additional set-aside". The moment `PENDING SELLS` joined the cash tickers,
   * proceeds the FIRM holds back from a sale under review were displayed to the advisor as their
   * own deliberate set-aside — attributing a process hold to a decision they never made.
   *
   * The same split already exists on the wizard's review page and in CRM's tracker. All three
   * have to agree about what is settled and what is waiting, or the advisor and the trade team
   * are reading different requests.
   *
   * ⚠️ ONE GROUP, and kept cash was briefly given its own before this (AndyP, 2026-09-18).
   * Splitting was only ever needed because the group printed "Cash set aside at the advisor's
   * request" against every child, which is false of a cash position nobody touched. Dropping that
   * line answers it: the children are named — `SchwabCash`, `SET-ASIDE CASH` — and the names carry
   * the difference. One group also answers the question a reader actually has, how much cash the
   * account ends up holding, with one figure rather than two they have to add.
   *
   * 🟢 The split that matters is the one that stays: settled versus PENDING, a balance versus a
   * job. Kept-vs-set-aside is the smaller difference and the ticker already states it.
   */
  get settledCashHoldings(): AccountRequestCustomHoldingModel[] {
    return this.customHoldings.filter(h => this.isSettledCashHolding(h));
  }

  get settledCashTotal(): number {
    return this.settledCashHoldings.reduce((sum, h) => sum + (h.estimatedValue ?? 0), 0);
  }

  /**
   * Is this line cash?
   *
   * ⚠️ THE FLAG FIRST, the ticker list only as a fallback (ACMTECH-983). `isCash` is what the
   * request recorded at filing time; the list is a guess from the name, and it can only ever
   * recognise the synthetic tickers this wizard invents. Cash the advisor KEEPS files under the
   * custodian's own ticker — CUSTODIAL_CASH, FDRXX, SWGXX — so the list never matched it and it
   * was drawn among the advisor's securities on both trackers.
   *
   * ⚠️ `?? `, not `||` or a truthy test. `false` is an ANSWER — the request said this line is a
   * security — and must not fall through to the ticker list. Only `null`/`undefined` may.
   *
   * ⚠️ Null is UNKNOWN, not false: requests filed before `req.CustomHolding.IsCash` existed carry
   * it for good, and nothing can recover it. Those keep the old behaviour exactly, including
   * kept cash still appearing among the securities. That is not a gap left unfixed — it is the
   * only honest reading of a request that never recorded the answer.
   */
  private isCashHolding(holding: AccountRequestCustomHoldingModel): boolean {
    return holding.isCash
      ?? RequestTrackerDetailModalComponent.CASH_TICKERS.has((holding.ticker ?? '').trim().toUpperCase());
  }

  /**
   * ⚠️ A SUBSET of {@link CASH_TICKERS}, never a replacement for it. Membership here decides only
   * which group a row is drawn in; every one of these must still be in CASH_TICKERS or it reads
   * as hand-typed and gets badged for review.
   */
  private isHeldCashHolding(holding: AccountRequestCustomHoldingModel): boolean {
    return RequestTrackerDetailModalComponent.HELD_CASH_TICKERS.has((holding.ticker ?? '').trim().toUpperCase());
  }

  /**
   * Settled cash — a balance, as opposed to {@link isHeldCashHolding}, which is a job.
   *
   * ⚠️ Takes whatever the custodian calls it. The two ticker lists name only the synthetic lines
   * this wizard writes; a real cash position arrives as CUSTODIAL_CASH, FDRXX, SchwabCash or
   * anything else, and lands here on the strength of the flag rather than its name.
   *
   * ⚠️ On requests filed before `IsCash` existed this is the synthetic lines ALONE, because a
   * null flag falls back to the ticker list and a custodian's ticker matches nothing there. Those
   * requests keep showing kept cash among the securities, exactly as they always did. Nothing
   * reclassifies them, because nothing can.
   */
  private isSettledCashHolding(holding: AccountRequestCustomHoldingModel): boolean {
    return this.isCashHolding(holding) && !this.isHeldCashHolding(holding);
  }

  /**
   * Describes what this request does to one requested sleeve, by pairing it with the same model in
   * the before-state. Returns null when there is nothing to say — either the sleeve is unchanged, or
   * no before-state was recorded and any claim would be invented.
   */
  /**
   * The word {@link allocationChange} returns for a sleeve with no before-state.
   *
   * ⚠️ A constant because the TEMPLATE colours on it. Comparing against a literal in two places
   * is how a rename turns a green label grey without anything failing.
   */
  private static readonly NEW_ALLOCATION = 'New';

  /** Whether this row's change is the sleeve being created, rather than a target moving. */
  isNewAllocation(sleeve: SleeveModel): boolean {
    return this.allocationChange(sleeve) === RequestTrackerDetailModalComponent.NEW_ALLOCATION;
  }

  allocationChange(sleeve: SleeveModel): string | null {
    // Empty currentSleeves means NOT RECORDED, not "the account had no sleeves". Every request filed
    // before ACMTECH-908 has none, and an Initial Account has no before-state by definition. Saying
    // "Added" against all of them would state something false.
    if (!this.currentSleeves.length) return null;

    // Paired on the model id, never the name: two models can share a name, and one can be renamed.
    // Either way the failure would be a silent mis-pairing rather than an error.
    const before = this.currentSleeves.find(c => c.investmentAccountModelID != null
      && c.investmentAccountModelID === sleeve.investmentAccountModelID);

    // "New", matching the badge on Review & Submit and on the CRM tracker. It said "Added",
    // which meant the same thing in a third word -- and these three views are read against each
    // other, so one vocabulary is worth more than the shade of difference between them.
    if (!before) return RequestTrackerDetailModalComponent.NEW_ALLOCATION;
    if (before.targetPercent == null || sleeve.targetPercent == null) return null;
    if (before.targetPercent === sleeve.targetPercent) return null;
    return `${formatPercent(before.targetPercent / 100, 'en-US', '1.0-2')} → ${formatPercent(sleeve.targetPercent / 100, 'en-US', '1.0-2')}`;
  }

  /**
   * Sleeves the account holds today that this request does not carry forward.
   *
   * ⚠️ Rendered inside the Allocations section rather than as a separate before-state list. A
   * closure the reader has to infer from an absence is one they will miss, and it is the only
   * instruction in the request with no row of its own to carry it.
   */
  get removedSleeves(): SleeveModel[] {
    return this.currentSleeves.filter(s => this.isRemovedSleeve(s));
  }

  /** A before-sleeve with no requested counterpart is being taken off the account. */
  isRemovedSleeve(sleeve: SleeveModel): boolean {
    if (!this.sleeves.length) return false;
    return !this.sleeves.some(s => s.investmentAccountModelID != null
      && s.investmentAccountModelID === sleeve.investmentAccountModelID);
  }

  closeDialogAndSendMessage(): void {
    this.dialogRef.close({ openMessage: true });
  }

  convertFileToMessagesAndFiles(file: FileModel): MessageAndFileModel {
    return {
      CreatedByUser: file.createdByUser ?? null,
      CreatedByUserName: file.createdByUserName ?? null,
      CreatedOn: file.createdOn ?? null,
      ExternalStatus: null,
      Note: null,
      FileName: file.fileName,
      FileID: file.fileID as unknown as string,
      StatusID: null,
    };
  }

  convertNoteToMessagesAndFiles(message: RequestNoteModel): MessageAndFileModel {
    return {
      CreatedByUser: message.createdByUser ?? null,
      CreatedByUserName: message.createdByUserName ?? null,
      CreatedOn: message.createdOn ?? null,
      ExternalStatus: message.externalStatus ?? null,
      Note: message.note ?? null,
      FileName: null,
      FileID: null,
      StatusID: message.statusID as number,
    };
  }

  sortNotesAndFiles(property: string, order: 'asc' | 'desc' = 'asc'): MessageAndFileModel[] {
    return this.notesAndFiles.sort((a, b) => {
      const aValue = a.CreatedOn;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const bValue = b.CreatedOn;

      if (aValue !== null && bValue !== null && aValue < bValue) {
        return order === 'asc' ? -1 : 1;
      } else if (aValue !== null && bValue !== null && aValue > bValue) {
        return order === 'asc' ? 1 : -1;
      } else {
        return 0;
      }
    });
  }

  setRequestDetailObject(detail: ContributionRequestDetailModel | DistributionRequestDetailModel | CloseAccountRequestDetailModel | ReallocationRequestDetailModel | GeneralAccountRequestDetailModel): void {

    this.files = detail.files;
    this.notes = detail.noteHistory;

    const convertedFiles = this.files.map(this.convertFileToMessagesAndFiles);
    const convertedNotes = this.notes.map(this.convertNoteToMessagesAndFiles);
    this.notesAndFiles = convertedFiles.concat(convertedNotes);
    this.notesAndFiles = this.sortNotesAndFiles('CreatedOn', 'desc');

    this.statusObj.Status = detail.status;
    this.statusObj.StatusDate = new Date(detail.statusDate).toLocaleDateString();
    this.statusObj.StatusNote = detail.statusNote;
    this.statusObj.StatusId = detail.statusId;

    this.details.set('Clients', '');
    this.details.set('Account #', detail.investmentAccountNumber);
    this.details.set('Account Type', detail.investmentAccountType);
    this.details.set('Submitted On', new Date(detail.submittedDate).toLocaleDateString() + ' - ' + new Date(detail.submittedDate).toLocaleTimeString());
    this.details.set('Submitted By', detail.submittedBy);

    if (this.data.category.includes(RequestTrackerTypeEnum.Reallocation)) {
      const m = detail as ReallocationRequestDetailModel;
      this.sleeves = m.sleeves;
      this.tradeInstructions = m.tradeInstructions;
      this.trades = m.trades;
    } else if (this.data.category.includes(RequestTrackerTypeEnum.Distribution)) {
      const d = detail as DistributionRequestDetailModel;
      this.details.set('Federal Tax Withholding', d.federalTax ? d.federalTax + '%' : 'N/A');
      this.details.set('State Tax Withholding', d.stateTax ? d.stateTax + '%' : 'N/A');
      this.details.set('Change Tax Withholdings', d.changeTaxWithholdings ? 'Yes': 'No');
      this.details.set('Frequency', d.frequency);
      this.details.set('Method', d.method);
      this.details.set('Delivery Instruction', d.deliveryInstruction ? d.deliveryInstruction : 'N/A');
      this.setCurrencyDetail('Gross Distribution Amount', d.grossAmount);
      this.details.set('Begins On', d.beginsOn ? new Date(d.beginsOn).toLocaleDateString() : 'N/A');
      this.sleeves = d.sleeves;
      this.tradeInstructions = d.tradeInstructions;
      this.trades = d.trades;
    } else if (this.data.category.includes(RequestTrackerTypeEnum.CloseAccount)) {
      const d = detail as CloseAccountRequestDetailModel;
      this.details.set('Federal Tax', d.federalTax ? d.federalTax + '%' : 'N/A');
      this.details.set('State Tax', d.stateTax ? d.stateTax + '%' : 'N/A');
      this.details.set('Change Tax Withholdings', d.changeTaxWithholdings ? 'Yes': 'No');
      this.details.set('Distribution Method', d.method);
      this.details.set('Delivery Instruction', d.deliveryInstruction ? d.deliveryInstruction : 'N/A');
      this.details.set('Bill As Of Close Date', d.isCloseDateBilled ? 'Yes' : 'No');

    } else if (this.data.category.includes(RequestTrackerTypeEnum.Contribution)) {
      const d = detail as ContributionRequestDetailModel;
      this.details.set('Frequency', d.frequency);
      this.details.set('Method', d.method);
      this.details.set('Delivery Instruction', d.deliveryInstruction ? d.deliveryInstruction : 'N/A');
      this.setCurrencyDetail('Gross Contribution Amount', d.grossAmount);
      this.setCurrencyDetail('Protected Cash Amount', d.protectedCashAmount);
      this.details.set('Begins On', d.beginsOn ? new Date(d.beginsOn).toLocaleDateString() : 'N/A');
      this.sleeves = d.sleeves;
      this.tradeInstructions = d.tradeInstructions;
    }  else if (this.data.category.includes(RequestTrackerTypeEnum.GeneralAccount)) {
      const d = detail as GeneralAccountRequestDetailModel;
      this.details.set('Custodian', d.custodian);
      this.details.set('Account Model', d.investmentAccountModel);
      // this.details.set('Total Account Fee', d.investmentAccountFee);
    }
    this.clients = detail.clients;

    this.note = detail.notes;

    if (this.sleeves.length > 0) {
      this.details.set('Allocations', '');
      this.getTotalSleeves();
    }
    if (this.trades.length > 0) {
      this.details.set('Trades', '');
    }
  }

  /**
   * Detail for a [req].AccountRequest. Section order mirrors the CRM modal so the two surfaces read
   * the same way. Cash figures arrive already recomputed - none of them are persisted - and are
   * pre-formatted here because the generic key/value loop in the template renders raw values.
   */
  setAccountRequestDetailObject(detail: AccountRequestDetailModel): void {
    this.files = detail.files;
    this.notes = detail.noteHistory;

    const convertedFiles = this.files.map(this.convertFileToMessagesAndFiles);
    const convertedNotes = this.notes.map(this.convertNoteToMessagesAndFiles);
    this.notesAndFiles = convertedFiles.concat(convertedNotes);
    this.notesAndFiles = this.sortNotesAndFiles('CreatedOn', 'desc');

    this.statusObj.Status = detail.status;
    this.statusObj.StatusDate = new Date(detail.statusDate).toLocaleDateString();
    this.statusObj.StatusNote = detail.statusNote;
    this.statusObj.StatusId = detail.statusId;

    this.clients = detail.clients;
    // the advisor's note from the wizard; drives the "Request Notes" panel, which
    // stays on its empty state when nothing was left
    this.note = detail.notes;

    this.details.set('Clients', '');
    this.details.set('Account #', detail.investmentAccountNumber);
    this.details.set('Account Type', detail.investmentAccountType);
    this.details.set('Submitted On', new Date(detail.submittedDate).toLocaleDateString() + ' - ' + new Date(detail.submittedDate).toLocaleTimeString());
    this.details.set('Submitted By', detail.submittedBy);
    // 🔴 OMITTED WHEN NULL, not printed as "No" (AndyP, 2026-10-05). The picker stopped asking who
    // initiated the request, so every request filed from now on carries null here. The old
    // ternary is falsy-based, so it would have labelled all of them "Solicited: No" — an answer
    // nobody gave, about a conversation nobody recorded, stated as fact on the trade team's own
    // screen.
    //
    // 🟢 Older requests still have a real true/false and still show it. The row disappearing is
    // what distinguishes "not asked" from both answers, the same way the Request Notes panel is
    // now suppressed rather than captioning a box that was never offered.
    if (detail.isSolicited != null) {
      this.details.set('Solicited', detail.isSolicited ? 'Yes' : 'No');
    }

    // FUNDING — the amount and how the money travels. Types 3 and 4 only; on 1 and 2 both blocks
    // are null, because the account itself is the pool and there is nothing being delivered.
    //
    // ⚠️ ALSO NULL ON A CONTRIBUTION OR DISTRIBUTION FILED BEFORE ACMTECH-990, which is NOT the
    // same as a request with no funding. Until that shipped the wizard collected every one of
    // these fields and `buildRequest()` discarded them at submit — those requests have no row and
    // nothing can recover one. Gated on the block being present rather than on the type id for
    // exactly that reason: an older request opens without these rows instead of printing a screen
    // of 'N/A' that reads as an advisor who answered nothing.
    //
    // 🟢 Plain key/value entries, so they render through the generic loop with no template change.
    // The labels are v1's, deliberately — the two generations are one request type to an advisor,
    // and the tracker should not make them read differently.
    if (detail.contribution) {
      const c = detail.contribution;
      this.details.set('Contribution Amount', formatCurrency(c.amount ?? 0, 'en-US', '$'));
      this.setDeliveryDetails(c);
      this.setFundingSleeves(c.sleeves, 'Into');
    }

    if (detail.distribution) {
      const d = detail.distribution;
      // A close leads with the fact that it is a close: it changes what every figure below means,
      // and the gross being the entire account is a consequence of it rather than a coincidence.
      if (d.isCloseAccount) {
        this.details.set('Closing the Account', 'Yes');
      }
      // ⚠️ BOTH AMOUNTS, and gross first. Gross is what leaves the account and is what the trade
      // team acts on; net is what the client was quoted. Neither is recoverable from the other
      // after the fact, because the account's withholding elections can be changed once a request
      // is filed — so they are shown rather than one being derived here.
      this.details.set('Gross Distribution Amount', formatCurrency(d.grossAmount ?? 0, 'en-US', '$'));
      this.details.set('Net Amount', formatCurrency(d.netAmount ?? 0, 'en-US', '$'));
      // v1's Distribution spelling rather than its Close Account one ('Federal Tax'). v1 uses both
      // for the same field; a V2 close IS a distribution, so it gets one spelling, the clearer one.
      this.details.set('Federal Tax Withholding', d.federalTaxPercent ? d.federalTaxPercent + '%' : 'N/A');
      this.details.set('State Tax Withholding', d.stateTaxPercent ? d.stateTaxPercent + '%' : 'N/A');
      this.setDeliveryDetails(d);
      // Only alongside the close, because that is the only time it means anything. Shown even when
      // false: it decides whether the client is billed, so 'No' is an answer worth printing.
      if (d.isCloseAccount) {
        this.details.set('Bill As Of Close Date', d.isCloseDateBilled ? 'Yes' : 'No');
      }
      this.setFundingSleeves(d.sleeves, 'From');
    }

    // WHERE A CHECK IS POSTED, read off the request root rather than the distribution because
    // that is where the table hangs it — a contribution could carry the same block one day
    // without a second table.
    //
    // 🔴 THE LABEL SAYS NEW, and that is the substance rather than decoration. A payee row exists
    // ONLY when the advisor typed the address, which they only do when Schwab holds none — so
    // every one of these is an address no custodian has verified, going out on a cheque. One
    // that Schwab does hold is never stored: it rides along on `Method` as part of the delivery
    // instruction, and the CRM tracker renders it in its own neutral block.
    //
    // ⚠️ Absent on a WIRE, which carries an uploaded instruction document instead; it shows
    // under Attachments below. An absent payee is not an incomplete wire.
    //
    // One row, not six. The grid is single values; an address has to be read whole to be checked
    // against anything, and splitting it over six labelled rows is how a reader ends up
    // comparing the city and never the street.
    //
    // 🔴 WHOSE ADDRESS IT IS LEADS THE LINE (AndyP, 2026-11-03), ahead of the name, because it
    // changes what the rest of the line means: a cheque to the client at an address Schwab has
    // never seen and a cheque to a stranger are the same four lines and completely different
    // things to approve. The advisor is asked it directly above the name for the same reason.
    //
    // ⚠️ The LABEL is printed, not `isThirdParty`. The lookup is editable from the CRM Lookups
    // screen, so renaming a row changes this line and nothing else — which is the right blast
    // radius for a label, and why nothing keys on it.
    if (detail.payee) {
      const p = detail.payee;
      const lines = [p.deliveryRecipientType, p.payeeName, p.streetAddress, p.addressContinued,
        `${p.city}, ${p.state} ${p.zip}`];
      this.details.set(
        'Payee (new address)',
        lines.filter((line) => !!line && line.trim().length).join(', '),
      );
    }

    // At-a-glance answer to "were any files sent with this request?". The files
    // themselves are listed, with downloads, in the timeline on the right.
    if (detail.files.length) {
      this.details.set('Attachments', `${detail.files.length} file${detail.files.length === 1 ? '' : 's'}`);
    }

    // ⚠️ ONE allocation section, and it leads. There were two: Current Allocation at the top
    // (the before-state) and Allocations at the bottom (the request), with the trades and the
    // totals between them -- so comparing what the account holds against what it will hold meant
    // scrolling past everything else and holding both in your head.
    //
    // They are now one table: the requested sleeves, each saying whether it is new or how its
    // target moved, then the ones being closed. The before-state is still the source of those
    // answers; it is just no longer a section of its own.
    //
    // Order matches Review & Submit -- allocation, then sold, then bought, then what is left.
    // The models are the decision; the trades are how it gets there, and the two surfaces are
    // read against each other.
    //
    // ⚠️ `currentSleeves` empty means NOT RECORDED, not "the account had no sleeves". An Initial
    // Account has no before-state and neither does anything filed before ACMTECH-908 captured
    // one, which is why `allocationChange` and `isRemovedSleeve` both return nothing rather than
    // claiming everything is new.
    this.currentSleeves = detail.currentSleeves;
    if (this.currentSleeves.length > 0) {
      this.getTotalCurrentSleeves();
    }

    this.sleeves = detail.sleeves;
    if (this.sleeves.length > 0) {
      this.details.set(this.sleevesTitle, '');
      this.getTotalSleeves();
    }

    // ⚠️ `?? []` because this arrives from a FIGData build that may predate the field, unlike
    // the three below it. A null here would throw in `totalCashReleased` rather than render an
    // empty section.
    this.releasedCash = detail.releasedCash ?? [];
    this.sells = detail.sells;
    this.buys = detail.buys;
    this.customHoldings = detail.customHoldings;
    // Cash first, ahead of the sells: it is the money that was already liquid, so the request
    // spends it before anything has to be sold. Same order as step 1 and as the CRM tracker.
    if (this.releasedCash.length > 0) {
      this.details.set(this.cashUsedTitle, '');
    }
    if (this.sells.length > 0) {
      this.details.set(this.positionsSoldTitle, '');
    }
    if (this.buys.length > 0) {
      this.details.set(this.positionsBoughtTitle, '');
    }
    if (this.customHoldings.length > 0) {
      this.details.set(this.customHoldingsTitle, '');
    }

    // ⚠️ THREE OF THESE FOUR WENT (AndyP, 2026-09-15), and only one for redundancy.
    //
    //   Sold Value    the Positions Being Sold section now carries its own Estimated Sell Total
    //   Total Value   the allocation section now totals models plus the sleeve to the account
    //
    // Both of those became duplicates the moment those totals were added, and a figure printed
    // twice on one screen invites the reader to check whether the two agree.
    //
    // ⚠️ INVESTABLE AMOUNT went for a different and better reason: it was WRONG to show here.
    // `investable` is a construction-time counter -- what the request had not yet placed while
    // the advisor was building it -- and the submit gate refuses anything left over, so on a
    // FILED request it is spent by definition. Printing it on the tracker implied money was
    // still sitting loose in a request where, by the rules that let it be submitted, none is.
    //
    // Custom Holdings Value stays: it is the sleeve's own figure as filed, not a residue.
    this.details.set('Custom Holdings Value', formatCurrency(detail.customHoldingsValue, 'en-US', '$'));

  }

  /**
   * The six delivery fields `req.Contribution` and `req.Distribution` share, rendered identically.
   *
   * One helper rather than two copies because the two tables genuinely agree here — the types
   * differ in WHAT is moved, never in HOW — and a copy is how the contribution's rows and the
   * distribution's drift into saying the same thing two ways.
   */
  private setDeliveryDetails(funding: {
    deliveryMethod?: string | undefined;
    deliveryFrequency?: string | undefined;
    deliveryInstruction?: string | undefined;
    deliveryStartDate?: Date | undefined;
    isOvernightDelivery?: boolean | undefined;
    hasIaAuthority?: boolean | undefined;
    deliveryRecipientType?: string | undefined;
  }): void {
    this.details.set('Method', funding.deliveryMethod ?? 'N/A');
    // Directly under the Method it qualifies: 'Check' and 'Third Party' are one decision read in
    // two lines, and separating them invites a reader to check one without the other.
    //
    // ⚠️ ONLY WHEN THE QUESTION WAS PUT, so no 'N/A' fallback. It is asked for Check and Wire
    // alone, and is absent on everything filed before ACMTECH-1036 — an 'N/A' row would read as
    // an answer nobody gave rather than a question nobody was asked.
    if (funding.deliveryRecipientType) {
      this.details.set('Sent To', funding.deliveryRecipientType);
    }
    this.details.set('Frequency', funding.deliveryFrequency ?? 'N/A');
    // ⚠️ 'N/A' here is a real answer, not a gap. Two delivery methods — Leave in Cash for Outgoing
    // Transfer, and Move to Retail — keep the money inside the custodian, so there is no account to
    // send it to and the wizard never asked for one.
    this.details.set('Delivery Instruction', funding.deliveryInstruction ? funding.deliveryInstruction : 'N/A');

    // Only when there is a schedule to begin. A one-time transfer stores no start date, and a row
    // reading 'Begins On — N/A' invites the reader to wonder what went missing.
    if (funding.deliveryStartDate) {
      this.details.set('Begins On', new Date(funding.deliveryStartDate).toLocaleDateString());
    }

    // Only when asked for. It applies to check delivery alone, so on an ACH request 'No' would be
    // answering a question nobody put.
    if (funding.isOvernightDelivery) {
      this.details.set('Overnight Delivery', 'Yes');
    }

    // ⚠️ `=== false`, NOT falsy. Null means Schwab did not say, which is a different claim from
    // 'no authority on file' — and it is the common case, so rendering it as a warning would put a
    // red flag on most requests. Stated only when Schwab actually said no, which is the case the
    // funding step itself warns on, by the same test.
    if (funding.hasIaAuthority === false) {
      this.details.set('IA Authority', 'Not on file');
    }
  }

  /**
   * Where a contribution's or distribution's money actually moves — one row per model sleeve.
   *
   * 🔴 NOT AN ALLOCATION, which is why these are their own rows rather than folded into the
   * Allocations table. That table answers where the account is AIMING; these answer how much
   * moves through each sleeve on this one request, leaving the account's targets untouched.
   * Conflating the two is what made a distribution read as though every model were being removed.
   *
   * ⚠️ Empty is legitimate and renders nothing — the whole amount can be raised from Custom
   * Holdings. It is also what every request filed before ACMTECH-990 carries, because the portal
   * collected these amounts and then discarded them at submit.
   */
  private setFundingSleeves(
    sleeves: AccountRequestFundingSleeveModel[] | undefined,
    direction: 'Into' | 'From',
  ): void {
    for (const sleeve of sleeves ?? []) {
      // Falls back to the id. A blank label would read as a sleeve with no model at all, which is
      // a different and more alarming thing than a name that could not be looked up.
      const label = sleeve.investmentAccountModel
        || (sleeve.investmentAccountModelID ? `Model ${sleeve.investmentAccountModelID}` : 'Unresolved model');
      const share = sleeve.percent != null ? ` (${sleeve.percent}%)` : '';
      this.details.set(
        `${direction} ${label}`,
        `${formatCurrency(sleeve.amount ?? 0, 'en-US', '$')}${share}`,
      );
    }
  }

  setPlanningDeskDetailObject(detail: PlanningDeskRequestDetailModel): void {
    this.files = detail.files;
    this.notes = detail.noteHistory;

    const convertedFiles = this.files.map(this.convertFileToMessagesAndFiles);
    const convertedNotes = this.notes.map(this.convertNoteToMessagesAndFiles);
    this.notesAndFiles = convertedFiles.concat(convertedNotes);
    this.notesAndFiles = this.sortNotesAndFiles('CreatedOn', 'desc');

    this.statusObj.Status = detail.status;
    this.statusObj.StatusDate = detail.statusDate ? new Date(detail.statusDate).toLocaleDateString() : 'N/A';
    this.statusObj.StatusId = detail.statusId;
    this.statusObj.StatusNote = detail.statusNote;

    this.details.set('Client', detail.client);
    this.details.set('Request Type', detail.requestType);
    if (detail.nextAppointment)
      this.details.set('Next Appointment', detail.nextAppointment ? new Date(detail.nextAppointment).toLocaleDateString() : 'N/A');
    this.details.set('Submitted On', new Date(detail.submittedDate).toLocaleDateString() + ' - ' + new Date(detail.submittedDate).toLocaleTimeString());
    this.details.set('Submitted By', detail.submittedBy);
    this.details.set('Updated On', detail.statusDate ? new Date(detail.statusDate).toLocaleDateString() : 'N/A');
    this.details.set('Status', detail.status);

    this.note = detail.notes;
    this.planningDeskAccounts = detail.accounts;
    if (this.planningDeskAccounts.length > 0) {
      this.details.set('Accounts', '');
      this.planningDeskAccounts.forEach(element => {
        element.clientFee = element.clientFee / 100;
      });
    }
  }

  setOrionServiceRequestDetailObject(detail: OrionServiceRequestModel2): void {
    console.log(detail);
    this.files = detail.files;
    this.notes = detail.noteHistory;

    const convertedFiles = this.files.map(this.convertFileToMessagesAndFiles);
    const convertedNotes = this.notes.map(this.convertNoteToMessagesAndFiles);
    this.notesAndFiles = convertedFiles.concat(convertedNotes);
    this.notesAndFiles = this.sortNotesAndFiles('CreatedOn', 'desc');

    this.statusObj.Status = detail.status;
    this.statusObj.StatusDate = new Date(detail.statusDate).toLocaleDateString();
    this.statusObj.StatusNote = detail.statusNote;
    this.statusObj.StatusId = detail.statusId;

    this.details.set('Clients', '');
    this.details.set('Account #', detail.investmentAccountNumber);
    this.details.set('Account Type', detail.investmentAccountType);
    this.details.set('Submitted On', new Date(detail.submittedDate).toLocaleDateString() + ' - ' + new Date(detail.submittedDate).toLocaleTimeString());
    this.details.set('Submitted By', detail.submittedBy);
    this.clients = detail.clients;

    if (detail.requestDetails.length) {

      const requestType = detail.requestDetails[0].requestType;
      this.details.set(requestType + ' Type', detail.requestDetails[0].requestSubType);

      if (detail.requestDetails[0].contributionAmount != null) {
        this.setCurrencyDetail('Gross Contribution Amount', detail.requestDetails[0].contributionAmount);
      }

      if (detail.requestDetails[0].distributionAmount != null) {
        this.setCurrencyDetail('Gross Distribution Amount', detail.requestDetails[0].distributionAmount);
      }

      if (detail.requestDetails[0].setupOption != null) {
        this.details.set('Setup Option', detail.requestDetails[0].setupOption);
      }

      if (detail.requestDetails[0].frequency != null) {
        this.details.set('Frequency', detail.requestDetails[0].frequency);
      }

      if (detail.requestDetails[0].startDate != null) {
        this.details.set('Start Date', new Date(detail.requestDetails[0].startDate).toLocaleDateString());
      }

      if (detail.requestComments.length) {
        detail.requestComments.forEach((comment: { note: string; }) => {
          this.note += comment.note + '\r\n\r\n';
        });
      }
    }
  }

  sortNull(): number {
    return 0;
  }
}
